import { useEffect, useState, useCallback } from "react";
import { MapView } from "./components/MapView";
import { useAccessToken } from "./hooks/useQueryParam";
import { useLiveTrackingSocket } from "./hooks/useLiveTrackingSocket";
import "./styles.css";
import { useUserLocation } from "./hooks/useUserLocation";
import { useMockVehicleLocation } from "./hooks/useMockVehicleLocation";

interface SessionInfoPanelProps {
  open: boolean;
  toggle: () => void;
  sessionName?: string;
  expiresAt?: string;
  points: number;
}

function SessionInfoPanel({
  open,
  toggle,
  sessionName,
  expiresAt,
  points,
}: SessionInfoPanelProps) {
  return (
    <div className={`session-panel ${open ? "open" : ""}`}>
      <button
        className="session-panel__toggle"
        onClick={toggle}
        aria-label="Toggle session info"
      >
        {open ? "▾" : "▴"}
      </button>
      <div className="session-panel__content">
        <div className="session-panel__title">
          {sessionName || "Shared Trip"}
        </div>
        {expiresAt && (
          <div className="session-panel__row">
            Expires: {new Date(expiresAt).toLocaleString()}
          </div>
        )}
        <div className="session-panel__row">Points: {points}</div>
      </div>
    </div>
  );
}

export default function App() {
  const token = useAccessToken();
  const params = new URLSearchParams(window.location.search);
  const mockVehicle = params.get("mockVehicle") === "1";
  const sessionIdParam = params.get("sessionId") || undefined;

  // Debug environment variables
  console.log("App: Environment check", {
    REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
    REACT_APP_WS_BASE_URL: process.env.REACT_APP_WS_BASE_URL,
    hasToken: !!token,
    sessionIdParam,
  });

  const liveTracking = useLiveTrackingSocket({
    url:
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_WS_BASE_URL ||
      undefined,
    sessionId: sessionIdParam,
    token: token || undefined,
    autoConnect: !!token, // token alone sufficient; server can derive session
  });

  const mock = useMockVehicleLocation({ intervalMs: 5000 });

  // Use Socket.IO-based live tracking when token is available
  const locations = mockVehicle ? mock.locations : liveTracking.locations;
  const latest = mockVehicle ? mock.latest : liveTracking.latest;
  const status = liveTracking.status;
  const error = liveTracking.error;

  // Removed client-side token validation fetch; server validates during WebSocket/Socket.IO handshake.

  const bannerClass = `status-banner ${status}` + (error ? " error" : "");
  const [panelOpen, setPanelOpen] = useState(false);
  const togglePanel = useCallback(() => setPanelOpen((o) => !o), []);
  // Real user location (set mock:true to simulate user as well if needed)
  const { location: userLocation } = useUserLocation({ mock: false });

  // Auto-open panel on desktop widths, collapse on small screens
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth >= 640) setPanelOpen(true);
      else setPanelOpen(false);
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    screen.orientation?.addEventListener?.("change", handleResize as any);
    return () => {
      window.removeEventListener("resize", handleResize);
      screen.orientation?.removeEventListener?.("change", handleResize as any);
    };
  }, []);

  return (
    <div className="app-container">
      <div className={bannerClass}>
        <span>Status: {status}</span>
        {error && <span>Error: {error}</span>}
        {!token && <span>No token provided</span>}
        {liveTracking.tokenValidated && <span>Token validated ✓</span>}
      </div>
      {!latest && (
        <div className="loader">
          {!token ? "Waiting for token..." : "Waiting for first location..."}
          {status === "error" && (
            <div style={{ marginTop: "10px", fontSize: "14px" }}>
              Connection troubleshooting:
              <ul style={{ textAlign: "left", marginTop: "5px" }}>
                <li>Is the backend server running on port 3500?</li>
                <li>Check browser console for detailed error messages</li>
                <li>
                  Try using HTTP instead of HTTPS for localhost development
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
      <MapView
        locations={locations}
        latest={latest}
        userLocation={userLocation}
      />
      {liveTracking.sessionId && liveTracking.vehicleKey && (
        <SessionInfoPanel
          open={panelOpen}
          toggle={togglePanel}
          sessionName={`Session ${liveTracking.sessionId}`}
          points={locations.length}
        />
      )}
    </div>
  );
}
